import BtnExcelDownload from "@/components/button/BtnExcelDownload";
import BtnMove from "@/components/button/BtnMove";
import SearchArea from "@/components/InstructionSearchArea";
import PageLayout from "@/components/layout/PageLayout";
import { useUser } from "@/contexts/UserContext";
import { useInstructionSearch } from "@/services/instructionService";
import { defaultPaginationQuery, SearchQuery } from "@/types/common";
import { InstructionSearchCondition } from "@/types/instruction";
import { Inbox as InboxIcon } from "@mui/icons-material";
import { Box } from "@mui/material";
import { DataGrid, GridColDef, GridOverlay, GridPaginationModel, GridRowParams, GridSortModel } from "@mui/x-data-grid";
import { useNavigate, useRouter } from "@tanstack/react-router";
import React, { useEffect, useMemo, useState } from "react";

interface DirectiveListPageProps {
  title: string;
}

const DirectiveListPage: React.FC<DirectiveListPageProps> = ({ title }) => {
  const navigate = useNavigate();
  const router = useRouter();
  const { user } = useUser();
  const role = (user?.role?.toLowerCase() ?? "approver") as "approver" | "manager" | "admin";
  const search = router.state.location.search;

  const [searchQuery, setSearchQuery] = useState<SearchQuery<InstructionSearchCondition>>(() => {
    const conditions = search.conditions as InstructionSearchCondition | undefined;
    return {
      page: (search.page as number) ?? defaultPaginationQuery.page,
      size: (search.size as number) ?? defaultPaginationQuery.size,
      sortBy: (search.sortBy as string) ?? defaultPaginationQuery.sortBy,
      direction: (search.direction as "ASC" | "DESC" | undefined) ?? defaultPaginationQuery.direction,
      conditions: conditions ?? {
        type: null,
        status: [],
      },
    };
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchQuery.page,
    pageSize: searchQuery.size,
  });

  const { data, isFetching } = useInstructionSearch(searchQuery);

  useEffect(() => {
    setSearchQuery((prev) => {
      if (prev.page === paginationModel.page && prev.size === paginationModel.pageSize) {
        return prev;
      }
      return { ...prev, page: paginationModel.page, size: paginationModel.pageSize };
    });
  }, [paginationModel]);

  useEffect(() => {
    setPaginationModel({ page: searchQuery.page, pageSize: searchQuery.size });
  }, [searchQuery.page, searchQuery.size]);

  const handleOnSearch = (searchQuery: SearchQuery<InstructionSearchCondition>) => {
    var type = searchQuery.conditions?.type ?? null;
    if (type === 0) {
      type = null;
    }

    const newSearchQuery = {
      ...searchQuery,
      conditions: {
        ...searchQuery.conditions,
        type: type,
        status: searchQuery.conditions?.status ?? [],
      },
    };

    setSearchQuery(newSearchQuery);

    // Update router search params
    navigate({
      to: "/instructions",
      search: (prev: any) => ({
        ...prev,
        conditions: newSearchQuery.conditions,
        page: newSearchQuery.page,
        size: newSearchQuery.size,
        sortBy: newSearchQuery.sortBy,
        direction: newSearchQuery.direction,
      }),
    });
  };

  const handleSortingModelChange = (model: GridSortModel) => {
    if (model.length === 0) {
      return;
    }

    var type = searchQuery.conditions?.type ?? null;
    if (type === 0) {
      type = null;
    }

    const sort = model[0];
    const newSearchQuery = {
      ...searchQuery,
      page: 0,
      sortBy: sort.field,
      direction: (sort.sort === "asc" ? "ASC" : "DESC") as "ASC" | "DESC",
      conditions: {
        ...searchQuery.conditions,
        type: type,
      },
    };

    setSearchQuery(newSearchQuery);

    // Update router search params
    navigate({
      to: "/instructions",
      search: (prev: any) => ({
        ...prev,
        conditions: newSearchQuery.conditions,
        page: newSearchQuery.page,
        size: newSearchQuery.size,
        sortBy: newSearchQuery.sortBy,
        direction: newSearchQuery.direction,
      }),
    });
  };

  // 타입별 컬럼 구성
  const columns = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "no",
        headerName: "No.",
        width: 80,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const rowIndex = params.api.getAllRowIds().indexOf(params.id);
          const totalCount = data?.pageInfo?.total ?? 0;
          const currentPage = searchQuery.page ?? 0;
          const pageSize = searchQuery.size ?? 10;

          // 유효하지 않은 값 체크
          if (totalCount <= 0 || rowIndex < 0) {
            return "-";
          }

          const rowNumber = totalCount - (currentPage * pageSize + rowIndex);
          return rowNumber > 0 ? rowNumber : "-";
        },
      },
      {
        field: "type",
        headerName: "지시유형",
        headerAlign: "center",
        align: "center",
        minWidth: 90,
      },
      {
        field: "title",
        headerName: "지시 상황",
        headerAlign: "center",
        minWidth: 200,
      },
      {
        field: "summary",
        headerName: "지시 요약",
        headerAlign: "center",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "ownerDepartment",
        headerName: "수명부서",
        headerAlign: "center",
        minWidth: 140,
      },
      {
        field: "ccDepartments",
        headerName: "협조부서",
        headerAlign: "center",
        sortable: false,
        minWidth: 215,
      },
      {
        field: "occurredDate",
        headerName: "발생일",
        headerAlign: "center",
        align: "center",
        minWidth: 100,
      },
      {
        field: "dueDate",
        headerName: "기한일",
        headerAlign: "center",
        align: "center",
        minWidth: 100,
      },
      {
        field: "closedDate",
        headerName: "종결일",
        headerAlign: "center",
        align: "center",
        minWidth: 100,
      },
      {
        field: "createdDate",
        headerName: "등록일",
        headerAlign: "center",
        align: "center",
        minWidth: 100,
      },
      {
        field: "createdName",
        headerName: "작성자",
        headerAlign: "center",
        align: "center",
        minWidth: 70,
      },
      {
        field: "status",
        headerName: "상태",
        headerAlign: "center",
        align: "center",
        minWidth: 90,
      },
    ];
    return baseColumns;
  }, [data?.pageInfo?.total, searchQuery.page, searchQuery.size]);

  // 권한별 버튼 구성
  const renderActionButtons = () => {
    return [
      <BtnExcelDownload
        key='excel'
        disabled={!data?.items.length}
        apiUrl={`/common/instructions/excel-export`}
        searchQuery={searchQuery}
      />,
      <BtnMove key='create_policy' text='방침 등록' to='/instructions/create' typeValue='POLICY' />,
      <BtnMove key='create_instruction' text='지시 등록' to='/instructions/create' typeValue='INSTRUCTION' />,
    ];
  };

  const handleRowClick = (params: GridRowParams) => {
    navigate({
      to: `/instructions/${params.id}`,
      search: router.state.location.search,
    });
  };

  return (
    <PageLayout title={title}>
      <SearchArea search={searchQuery} onSearch={handleOnSearch} role={role} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>{renderActionButtons()}</Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <DataGrid
          rows={data?.items ?? []}
          columns={columns}
          paginationModel={paginationModel}
          rowCount={data?.pageInfo.total ?? 0}
          paginationMode='server'
          onPaginationModelChange={setPaginationModel}
          sortingMode='server'
          onSortModelChange={handleSortingModelChange}
          loading={isFetching}
          disableRowSelectionOnClick
          disableColumnMenu
          pageSizeOptions={[25, 50, 100]}
          onRowClick={handleRowClick}
          rowHeight={48}
          slots={{
            noRowsOverlay: () => (
              <GridOverlay>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    fontSize: "18px",
                    color: "#888",
                    gap: "8px",
                  }}
                >
                  <InboxIcon sx={{ fontSize: 40, color: "#888" }} />
                  표시할 데이터가 없습니다.
                </div>
              </GridOverlay>
            ),
          }}
          sx={{
            "& .MuiDataGrid-main": { overflowY: "hidden" },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-row:hover": {
              cursor: "pointer",
            },
          }}
        />
      </Box>
    </PageLayout>
  );
};

export default DirectiveListPage;
